import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { AppRoutes, useProjectId } from '../../../app-routes';
import * as formService from '../../form-services/incentive-editor';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { ProjectAssessmentApiClient } from '../../../../services/project-assessment';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { BwjValidityState } from '../../../../framework/entities/bwj-validity-state';
import { BwjUploadField } from '../../../../framework/components/bwj-upload';
import { BwjImageBrowser, IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { isNotNullOrWhitespace } from '../../../../framework/utils';
import { ImageApiClient } from '../../../../services/image';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { BwjText, BwjTextSize } from '../../../../framework/components/bwj-text';
import { useNavigator } from '../../../../framework/services/navigation';
import { IProjectIncentiveEditingModel, ProjectIncentiveEditingModel } from '../../../../entities-generated/models/project/project-incentive-editing-model';

const subscriberId = 'Page.ManagePurchaseIncentives';

export const PageManagePurchaseIncentives: React.FunctionComponent = () => {
  const nav = useNavigator();
  const { showToast } = useToast();
  const projectId = useProjectId();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Preparing assessment...');
  const errorState = useStandardErrorState();
  const formModel = formService.useFormModel(subscriberId);
  const taxFormImage = useMemo(() => {
    return isNotNullOrWhitespace(formModel.taxExemptionFormUrl)
      ? [{ src: formModel.taxExemptionFormUrl }] : [];
  }, [formModel]);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, true);
  const editorModel = useRef<IProjectIncentiveEditingModel>(new ProjectIncentiveEditingModel());

  const showLoadingView = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }

  useEffect(() => {
    showLoadingView('Loading assessment...');

    ProjectAssessmentApiClient.getLiveProject(projectId)
    .then(resp => {
      editorModel.current = resp;
      formService.initializeService(resp);
      setViewState(BwjViewState.Ready);
    })
    .catch(e => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, [projectId]);

  const onClearTaxForm = (image: IBrowserImage) => {
    ImageApiClient.removeImage(image.src)
    .then(() => {
      formService.taxExemptionFormUrl.onUpdate('');
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Form removal failed');
    });
  };

  const onUpdateIncentives = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return undefined; }

      showLoadingView('Saving assessment...');
      const request = formService.toUpdateRequest(false);
      return ProjectAssessmentApiClient.updatePurchaseIncentives(request, projectId)
        .then(() => {
          return {};
        });
    })
    .then(resp => {
      if(resp === undefined) { return; }

      showToast(MessageBarType.success, 'Assessment successfully recorded');
      nav.goTo(AppRoutes.openProjects.url());
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - save failed');
      setViewState(BwjViewState.Ready);
    });
  }

  return (
    <>
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: loadingMessage.current
      }}
      errorState={errorState}
    >
      <BwjPageHeader
        title={'Manage Purchase Incentives'}
        titleIconName={AppRoutes.managePurchaseIncentives.icon}
        actionButtons={[{
          id: 'btnSave',
          name: 'Save incentives',
          iconName: 'Save',
          disabled: hasUnsavedChanges === false,
          onClick: onUpdateIncentives,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={8}>
              <BwjUploadField
                id={formService.taxExemptionFormUrlUploader.id}
                required={false}
                label={formService.taxExemptionFormUrlUploader.description}
                onUpdate={formService.taxExemptionFormUrlUploader.onUpdate}
                input={formModel.taxExemptionFormUrlUploader}
                accept={['jpg', 'jpeg', 'png']}
                disabled={formModel.taxExemptionFormUrlUploader.validity === BwjValidityState.Indeterminate}
                allowMultipleFiles={false}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjImageBrowser
                id={formService.taxExemptionFormUrl.id}
                images={taxFormImage}
                displayIndex={0}
                loadImages={true}
                imageWidth={400}
                contextButton={{
                  icon: 'Delete',
                  onClick: onClearTaxForm,
                }}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.discounts.id}
                required={false}
                label={formService.discounts.description}
                onUpdate={formService.discounts.onUpdate}
                input={formModel.discounts}
                multiline={true}
              />
              <BwjText
                textSize={BwjTextSize.xSmall}
              >
                Format: [description/code]%[percentage discount (optional)]|[flat discount (optional)]&gt;YYYY-M-D
              </BwjText>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.notes.id}
                required={false}
                label={formService.notes.description}
                onUpdate={formService.notes.onUpdate}
                input={formModel.notes}
                multiline={true}
              />
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};